import { useEffect, useRef, useState } from 'react';
import { joinPresence, type Peer, type PresenceHandle } from '../../data/presence';
import type { Editor } from '../editor';

/**
 * 접속자 표시 (작업지시서 P4-13)
 *
 * 같은 버전을 보고 있는 사람을 머리말에 띄우고, 내가 선택한 객체를
 * "잡고 있다" 고 방송한다. 남이 잡은 것을 편집하려 하면 명령이 막힌다.
 */
export function PresenceBar({
  editor,
  versionId,
  me,
}: {
  editor: Editor;
  versionId: string;
  me: { email: string; name: string };
}) {
  const [peers, setPeers] = useState<Peer[]>([]);
  const handle = useRef<PresenceHandle | null>(null);

  useEffect(() => {
    const h = joinPresence({
      versionId,
      me,
      onChange: () => setPeers(h.peers()),
    });
    handle.current = h;
    editor.presence = h;

    return () => {
      editor.presence = null;
      h.close();
      handle.current = null;
    };
  }, [versionId, me.email, me.name, editor]);

  // 선택이 바뀌면 "내가 잡은 것" 을 방송한다
  useEffect(
    () =>
      editor.subscribe(() => {
        handle.current?.setHolding([...editor.selection].sort());
      }),
    [editor]
  );

  if (peers.length === 0) return null;

  return (
    <span className="presence" title="같은 버전을 보고 있는 사람">
      {peers.slice(0, 5).map((p) => (
        <span
          key={p.email}
          className="presence__dot"
          style={{ background: p.color }}
          title={`${p.name} (${p.email})${p.holding.length > 0 ? ` — ${p.holding.length}개 편집 중` : ''}`}
        >
          {p.name.slice(0, 1)}
        </span>
      ))}
      {peers.length > 5 && <span className="presence__more">+{peers.length - 5}</span>}
    </span>
  );
}
